import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Analytics as VercelAnalytics } from '@vercel/analytics/react';
import { useAuth } from './context/AuthContext';

// Layouts
import AuthLayout from './components/layouts/AuthLayout';
import DashboardLayout from './components/layouts/DashboardLayout';
import AdminLayout from './components/layouts/AdminLayout';
import SubscriptionLayout from './components/layouts/SubscriptionLayout';
import PlanwiseLayout from './components/layouts/PlanwiseLayout';

// Auth Pages
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import AcceptInvitation from './pages/auth/AcceptInvitation';
import RegistrationSuccess from './pages/auth/RegistrationSuccess';
import RegistrationCancel from './pages/auth/RegistrationCancel';

// Dashboard Pages
import Dashboard from './pages/dashboard/Dashboard';
import Calendar from './pages/dashboard/Calendar';
import RequestLeave from './pages/dashboard/RequestLeave';
import LeaveHistory from './pages/dashboard/LeaveHistory';
import LeaveApprovals from './pages/dashboard/LeaveApprovals';
import TeamSettings from './pages/dashboard/TeamSettings';
import Profile from './pages/dashboard/Profile';
import Analytics from './pages/dashboard/Analytics';
import OurPeople from './pages/dashboard/OurPeople';
import WhosOut from './pages/dashboard/WhosOut';
import Planwise from './pages/Planwise';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminTeams from './pages/admin/AdminTeams';
import AdminUsers from './pages/admin/AdminUsers';
import AdminPayments from './pages/admin/AdminPayments';
import AdminLogs from './pages/admin/AdminLogs';
import AdminEmails from './pages/admin/AdminEmails';

// Payment Pages
import Subscription from './pages/payment/Subscription';
import PaymentSuccess from './pages/payment/PaymentSuccess';
import PaymentCancel from './pages/payment/PaymentCancel';

// Legal Pages
import TermsOfService from './pages/legal/TermsOfService';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, user } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  const { isAuthenticated, user, checkAuth } = useAuth();
  
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);
  
  return (
    <>
      <Routes>
        {/* Auth Routes */}
        <Route path="/" element={<AuthLayout />}>
          <Route index element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} />
          <Route path="login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} />
          <Route path="signup" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Signup />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password/:token" element={<ResetPassword />} />
          <Route path="invitation/:token" element={<AcceptInvitation />} />
        </Route>
        
        {/* Full-Width Planwise Route */}
        <Route path="/dashboard/planwise" element={
          <ProtectedRoute>
            <PlanwiseLayout>
              <Planwise />
            </PlanwiseLayout>
          </ProtectedRoute>
        } />

        {/* Dashboard Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Dashboard />} />
          <Route path="calendar" element={<Calendar />} />
          <Route path="whos-out" element={<WhosOut />} />
          <Route path="request-leave" element={<RequestLeave />} />
          <Route path="leave-history" element={<LeaveHistory />} />
          {/* Leave Approvals is accessible to team_admin AND to department
              managers (derived on the server). The page itself renders an
              empty list for anyone who shouldn't see anything, so role-gate
              is intentionally broad. */}
          <Route path="leave-approvals" element={<LeaveApprovals />} />
          <Route path="analytics" element={
            <ProtectedRoute allowedRoles={['team_admin']}>
              <Analytics />
            </ProtectedRoute>
          } />
          <Route path="our-people" element={
            <ProtectedRoute allowedRoles={['team_admin']}>
              <OurPeople />
            </ProtectedRoute>
          } />
          <Route path="profile" element={<Profile />} />
          <Route path="profile/:userId" element={<Profile />} />
          <Route path="team-settings" element={
            <ProtectedRoute allowedRoles={['team_admin']}>
              <TeamSettings />
            </ProtectedRoute>
          } />
        </Route>
        
        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminLayout />
          </ProtectedRoute>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="teams" element={<AdminTeams />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="logs" element={<AdminLogs />} />
          <Route path="emails" element={<AdminEmails />} />
        </Route>
        
        {/* Payment Routes */}
        <Route path="/subscription" element={
          <ProtectedRoute>
            <SubscriptionLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Subscription />} />
        </Route>
        <Route path="/payment-success" element={
          <ProtectedRoute>
            <SubscriptionLayout />
          </ProtectedRoute>
        }>
          <Route index element={<PaymentSuccess />} />
        </Route>
        <Route path="/payment-cancel" element={
          <ProtectedRoute>
            <SubscriptionLayout />
          </ProtectedRoute>
        }>
          <Route index element={<PaymentCancel />} />
        </Route>
        
        {/* Registration Payment Routes - No auth required */}
        <Route path="/registration-success" element={<RegistrationSuccess />} />
        <Route path="/registration-cancel" element={<AuthLayout />}>
          <Route index element={<RegistrationCancel />} />
        </Route>
        
        {/* Legal Pages - No auth required */}
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#22c55e',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <VercelAnalytics />
    </>
  );
}

export default App;
